import { z } from "zod";
/**
 * WHMA memory interface contracts. recall_recent_fires and get_current_goal are
 * the two memory tools; these schemas are the strict-JSON shapes the tool layer
 * validates on the way out (fail-closed on violation).
 */
export const HistoryFireResponse = z.enum(["consumed", "dismissed", "completed", "ignored"]);
export const HistoryJourneyState = z.enum(["on_track", "doing_fine", "struggling", "disengaged"]);
/** A single surfaced fire, in the compact shape the agent/eval consume. */
export const HistoryFire = z.object({
    id: z.string(),
    eventType: z.string(),
    surface: z.string(),
    journeyState: HistoryJourneyState.nullable(),
    /** Lens names that contributed to the decision, most significant first. */
    lensContributions: z.array(z.string()),
    response: HistoryFireResponse.nullable(),
    createdAt: z.string(),
    respondedAt: z.string().nullable(),
    /** Decay-weighted retrieval salience in (0, 1]; fresh = 1, fades by half each
     *  half-life. Lets the agent read recency-weighted importance, not raw order. */
    weight: z.number(),
});
export const RecallRecentFiresQuery = z.object({
    days: z.coerce.number().int().min(1).max(90).default(7),
});
/** `limit` = leaves per memory tier on the WHMA graph. The default keeps the
 *  ambient map legible; the constellation view asks for a larger slice. */
export const WhmaGraphQuery = z.object({
    // Matches the agent's own recall, so the map shows what the agents are told
    // rather than a shorter version of it.
    limit: z.coerce.number().int().min(1).max(48).default(12),
});
export const RecallRecentFiresResponse = z.object({
    fires: z.array(HistoryFire),
    windowDays: z.number(),
});
/** The user's current (active) goal, compacted for the memory tool. */
/** WHMA data-health: how much the system actually knows about this user, and
 *  where on the prior -> personal handover they sit. */
export const DataHealthComponents = z.object({
    signalCoverage: z.number(),
    signalRecency: z.number(),
    engagement: z.number().nullable(),
    volume: z.number(),
});
export const DataHealthResponse = z.object({
    score: z.number(),
    basis: z.enum(["priors", "blended", "personal"]),
    personalWeight: z.number(),
    observations: z.number(),
    components: DataHealthComponents,
    habituation: z.array(z.string()),
    /** Unresolved derived-fact contradictions; nonzero docks the score. */
    conflicts: z.number(),
});
/** WHMA derived facts. */
export const WhmaFactCategory = z.enum(["preference", "pattern", "constraint", "context"]);
export const WhmaFact = z.object({
    id: z.string(),
    key: z.string(),
    value: z.string(),
    /** Short human display name for the fact ("Weight goal", "Morning person").
     *  Nullable for facts extracted before the field existed. */
    label: z.string().nullable(),
    /** Short value chip for the memory-graph node ("6.1h", "9.7k"). Null when no
     *  number in the extraction material quantified the fact. */
    mono: z.string().nullable().optional(),
    /** Episodic provenance: history_fires ids grounding the fact. Citations are
     *  validated against a provided evidence index, so graph "evidences" edges
     *  are stored links, never prose. */
    sourceFireIds: z.array(z.string()).nullable().optional(),
    category: WhmaFactCategory,
    confidence: z.number(),
    source: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const RecallFactsResponse = z.object({
    facts: z.array(WhmaFact),
});
/** Member-authored memory writes from the fingerprint graph. Stored with the
 *  same semantics as Prybar's memory.remember: source "user", confidence 0.95,
 *  supersede on restatement, retire (never hard-delete) on removal. */
export const CreateUserFactRequest = z.object({
    /** Short display name, 1 to 3 words ("Morning walks"). Also derives the key. */
    label: z.string().min(1).max(40),
    /** The memory in one sentence. */
    statement: z.string().min(1).max(300),
    category: WhmaFactCategory.default("context"),
});
export const UpdateUserFactRequest = z.object({
    statement: z.string().min(1).max(300),
    label: z.string().min(1).max(40).optional(),
    category: WhmaFactCategory.optional(),
});
export const UserFactParams = z.object({ id: z.string().uuid() });
export const UserFactResponse = z.object({ fact: WhmaFact });
/** One memory per subject: a create that collides with an existing memory
 *  returns 409 with the memory to edit instead. */
export const UserFactConflictResponse = z.object({
    error: z.string(),
    existing: z.object({
        id: z.string(),
        label: z.string().nullable(),
        value: z.string(),
    }),
});
export const DeleteUserFactResponse = z.object({ deleted: z.boolean() });
/** The memory snapshot rendered as Markdown helper-context for a prompt. */
export const MemoryContextResponse = z.object({
    context: z.string(),
});
/** Strict-JSON shape the extraction LLM must return; validated fail-closed. */
export const ExtractedFact = z.object({
    key: z.string().min(1).max(64),
    value: z.string().min(1).max(200),
    /** Short human display name a member reads on their memory graph: 1 to 3
     *  words, Title case, naming the trait ("Weight goal", "Nausea-prone"). */
    label: z.string().min(1).max(40),
    /** Short value chip quantifying the fact ("6.1h", "9.7k"), copied from a
     *  number in the material. Null when nothing quantifies it. Sanitised in
     *  code before storage. */
    mono: z.string().max(12).nullable(),
    /** Keys from the provided "Recent moments" evidence index (e.g. "M1") that
     *  ground this fact. Empty when none apply. Validated in code: unknown keys
     *  are dropped and the rest capped at MAX_FACT_EVIDENCE in code, so
     *  provenance is referentially sound. No count bound here: strict mode
     *  cannot send one, so the model never saw it and a fifth citation failed
     *  the whole extraction. */
    evidence: z.array(z.string().max(8)),
    category: WhmaFactCategory,
    confidence: z.number().min(0).max(1),
});
export const ExtractedFacts = z.object({
    facts: z.array(ExtractedFact).max(20),
});
export const ExtractFactsResult = z.object({
    inserted: z.number(),
    overlapped: z.number(),
    superseded: z.number(),
    conflicts: z.number(),
    total: z.number(),
});
/** A held contradiction: an incoming fact that disputes the current active one. */
export const FactConflict = z.object({
    incoming: WhmaFact,
    existing: WhmaFact.nullable(),
});
export const FactConflictsResponse = z.object({
    conflicts: z.array(FactConflict),
});
export const ResolveConflictRequest = z.object({
    resolution: z.enum(["keep_existing", "keep_incoming"]),
});
export const ResolveConflictResponse = z.object({
    resolved: z.boolean(),
});
//# sourceMappingURL=history.js.map